import { useCallback, useMemo } from 'react';
import type { ScaleTime } from 'd3-scale';
import { isBefore, startOfDay } from 'date-fns';
import {
  getDateRangeByPeriod,
  type Period,
} from '@/utilities/timeSeriesPeriodMap';

type Valuation = {
  fairValue: number | null;
  createdAt: string;
};

type Props = {
  xScale: ScaleTime<number, number>;
  valuations: Valuation[] | null | undefined;
  period: Period;
};

export const useNarrativeValuationHistory = ({
  valuations,
  xScale,
  period,
}: Props) => {
  const filteredValuations = useMemo(() => {
    const { startDate } = getDateRangeByPeriod(period);
    return (valuations ?? []).reduce((result, valuation, index) => {
      const date = new Date(valuation.createdAt);
      const isValuationWithinPeriod = !isBefore(date, startDate);

      if (isValuationWithinPeriod) {
        // We are adding the previous valuation as the first valuation is unlikely to line up with the period start
        if (result.length === 0 && index > 0 && valuations) {
          result.push(valuations[index - 1]);
        }
        result.push(valuation);
      }

      return result;
    }, [] as Valuation[]);
  }, [valuations, period]);

  const { valuationsByDate, valuationTimestamps } = useMemo(() => {
    const oldestValuation = filteredValuations.at(-1);
    const newestValuation = filteredValuations.at(0);
    const domainStart = xScale.domain()[0].getTime();
    const domainEnd = xScale.domain()[1].getTime();

    const valuationsByDate: Record<number, number | null> =
      filteredValuations.reduce(
        (acc, v) => {
          acc[startOfDay(new Date(v.createdAt)).getTime()] = v.fairValue;
          return acc;
        },
        {
          [domainStart]: oldestValuation?.fairValue ?? null,
          [domainEnd]: newestValuation?.fairValue ?? null,
        }
      ) ?? {};
    const valuationTimestamps = Object.keys(valuationsByDate)
      .map(Number)
      .sort();

    return { valuationsByDate, valuationTimestamps };
  }, [filteredValuations, xScale]);

  const findClosestFairValue = useCallback(
    (selectedTimestamp: number) => {
      const selectedFairValueTimestamp = valuationTimestamps.reduce(
        (result, current) => (selectedTimestamp >= current ? current : result),
        valuationTimestamps[0]
      );
      const lastValuationTimestamp = valuationTimestamps.at(-1);

      if (selectedFairValueTimestamp)
        return valuationsByDate[selectedFairValueTimestamp];

      return lastValuationTimestamp
        ? valuationsByDate[lastValuationTimestamp]
        : null;
    },
    [valuationTimestamps, valuationsByDate]
  );

  return {
    findClosestFairValue,
    valuationsByDate,
    valuationTimestamps,
  };
};
